/**
 * Organic vein & root SVG paths for the Living Tree.
 *
 * Design inspired by bioluminescent energy flowing through a real tree —
 * winding, curving tendrils that twist through the trunk, fork into
 * the canopy, and spread underground as gnarled roots.
 *
 * All paths use cubic Bézier curves for natural, flowing motion.
 * ViewBox: 0 0 100 110
 */

// ─── TRUNK: Main energy channel winding up the centre ───────
export const TRUNK_VEINS = [
  // Primary trunk — slight S-curve through the centre
  'M50 98 C49 92 48 86 47.5 80 C47 74 48 68 49 62 C50 56 51 50 50.5 44 C50 38 49 32 50 26',
  // Secondary trunk — slightly offset, thinner
  'M51 97 C52 90 51.5 83 50.5 77 C49.5 71 48.5 65 49.5 59 C50.5 53 52 47 51.5 41 C51 35 50 30 50.5 24',
];

// ─── BRANCHES: Veins forking into the canopy ────────────────
export const BRANCH_VEINS = [
  // Left primary — sweeping curve to upper-left
  'M49 58 C46 53 42 48 38 44 C34 40 29 37 24 34 C20 31 17 29 14 27',
  // Right primary — sweeping curve to upper-right
  'M50 58 C53 53 57 48 61 44 C65 40 70 37 75 34 C79 31 82 29 85 27',
  // Left secondary — higher fork
  'M49.5 44 C47 40 43 36 39 33 C35 30 30 27 26 25 C23 23 20 21 17 20',
  // Right secondary — higher fork
  'M50.5 44 C53 40 57 36 61 33 C65 30 70 27 74 25 C77 23 80 21 83 20',
  // Left upper canopy — graceful curl
  'M49 38 C46 34 42 30 38 27 C34 24 30 22 26 20 C23 18 20 16 18 15',
  // Right upper canopy — graceful curl
  'M51 38 C54 34 58 30 62 27 C66 24 70 22 74 20 C77 18 80 16 82 15',
  // Left mid-branch — shorter, curves outward
  'M47 52 C43 49 39 47 35 45 C31 43 27 42 23 42',
  // Right mid-branch — shorter, curves outward
  'M52 52 C56 49 60 47 64 45 C68 43 72 42 76 42',
  // Left lower branch — sweeps wide
  'M48 64 C44 61 39 58 34 56 C29 54 24 53 19 52',
  // Right lower branch — sweeps wide
  'M51 64 C55 61 60 58 65 56 C70 54 75 53 80 52',
  // Left tendril — delicate upper wisp
  'M38 44 C35 41 32 38 28 36',
  // Right tendril — delicate upper wisp
  'M61 44 C64 41 67 38 71 36',
  // Left canopy tip
  'M26 25 C23 22 20 19 16 17',
  // Right canopy tip
  'M74 25 C77 22 80 19 84 17',
];

// ─── ROOTS: Gnarled, spreading underground tendrils ─────────
export const ROOT_VEINS = [
  // Central taproot — goes straight down with slight curve
  'M50 98 C50 100 49.5 102 49 104 C48.5 106 49 108 50 110',
  // Left primary root — wide spreading curve
  'M50 97 C47 99 43 100 39 101 C34 102 28 103 22 103 C17 103 13 102 8 101',
  // Right primary root — wide spreading curve
  'M50 97 C53 99 57 100 61 101 C66 102 72 103 78 103 C83 103 87 102 92 101',
  // Left secondary — dips deeper then curves
  'M49 98 C45 100 41 102 37 104 C33 106 28 107 23 107 C19 107 15 106 11 105',
  // Right secondary — dips deeper then curves
  'M51 98 C55 100 59 102 63 104 C67 106 72 107 77 107 C81 107 85 106 89 105',
  // Left fine root — curling tendril
  'M39 101 C36 103 32 105 28 106 C24 107 20 107 16 106',
  // Right fine root — curling tendril
  'M61 101 C64 103 68 105 72 106 C76 107 80 107 84 106',
  // Left deep root — goes down and curves back
  'M47 99 C43 101 39 104 36 107 C34 109 31 110 28 110',
  // Right deep root — goes down and curves back
  'M53 99 C57 101 61 104 64 107 C66 109 69 110 72 110',
];

export const ALL_VEIN_PATHS = [...TRUNK_VEINS, ...BRANCH_VEINS, ...ROOT_VEINS];

/** Paths used for energy particle flow animation */
export const ENERGY_PATHS = [
  TRUNK_VEINS[0],
  BRANCH_VEINS[0],
  BRANCH_VEINS[1],
  BRANCH_VEINS[2],
  BRANCH_VEINS[3],
];

/** Determine which path group an index belongs to */
export function getPathType(index: number): 'trunk' | 'branch' | 'root' {
  if (index < TRUNK_VEINS.length) return 'trunk';
  if (index < TRUNK_VEINS.length + BRANCH_VEINS.length) return 'branch';
  return 'root';
}

/** Get stroke width based on path type */
export function getPathWidth(index: number): number {
  const type = getPathType(index);
  if (type === 'trunk') return 1.2;
  if (type === 'root') return 0.7;
  // Branches get thinner toward tips
  const branchIndex = index - TRUNK_VEINS.length;
  if (branchIndex < 6) return 0.55; // primary branches
  if (branchIndex < 10) return 0.4; // mid branches
  return 0.3; // tendrils
}

/** Get glow color based on path type */
export function getPathColor(index: number, opacity: number): string {
  const type = getPathType(index);
  if (type === 'root') return `hsla(35, 70%, 48%, ${opacity})`;
  if (type === 'trunk') return `hsla(42, 85%, 55%, ${opacity})`;
  return `hsla(45, 80%, 58%, ${opacity})`;
}
